import { Controller } from "@hotwired/stimulus"
import { autoUpdate, computePosition, flip, hide, offset, shift } from "@floating-ui/dom"

// Things a click is meant to OPERATE rather than comment on. `.course-review-add` and
// `.course-review-marker` are the review layer's own controls and are handled
// separately, so they are deliberately absent here.
const CONTROL_SELECTOR = 'button, input, select, textarea, label, summary, a[href], [draggable="true"]'

// How long the canvas settles before the first-run nudge appears — long enough that
// the reviewer has registered the page, short enough to still read as "on open".
const NUDGE_DELAY = 600

// How long the landing flash rings the element it dropped the reviewer on. Matches the
// 1.6s `course-review-focus-flash` keyframes plus a beat, and the builder's own
// `revealReviewAnchor` timeout — one deep link, one length, wherever it is followed.
const LANDING_FLASH_MS = 1800

// How long the pointer has to rest on a part of an element before that part takes the
// hover from the element around it. Short enough to feel immediate when the reviewer
// means the part; long enough that a pointer only passing over it on its way to the
// element's own ⊕ does not take the hover — and the ⊕ with it — away.
const NESTED_HOVER_REST_MS = 150

// Review mode's in-canvas layer: it decorates every commentable element, opens the
// floating composer on the ⊕ chip, and opens the thread popover on an element that
// already has comments.
//
// The composer and the thread are appended to <body>, not to the canvas, because
// the canvas scrolls and clips — a floating card anchored near its edge would be cut
// off. Floating UI (the same mechanism the slash menu uses) keeps them on-screen.
export default class extends Controller {
  static targets = [
    "composerTemplate", "addButtonTemplate", "threadTemplate", "threadSectionTemplate",
    "threadItemTemplate", "threadAttachmentTemplate", "nudgeTemplate"
  ]
  static values = {
    createUrl: String,
    attachmentUrl: String,
    courseId: String,
    anchors: Object,
    mentions: Array,
    mentionsSearchUrl: String,
    // Reviewers may open new threads (the ⊕ chip); the author reads and replies but
    // never opens top-level threads on their own course, so the chip is withheld.
    canComment: Boolean,
    // Keys the first-run nudge's dismissal, so two reviewers sharing a browser each
    // get their own.
    viewerId: String,
    // The anchor key this page load was sent to open ON (TMS-1439) — the Review inbox
    // landing on a course's oldest pending comment, or a Review Activity row followed
    // from the preview. Empty on an ordinary visit.
    landingAnchor: String
  }

  connect() {
    this.decorateAnchors()
    this.onDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.onDocumentClick)
    // `mouseover` as well as `mousemove`: the browser fires it when scrolling puts a
    // different element under a resting pointer, which `mousemove` alone would miss.
    this.onMouseMove = this.trackHover.bind(this)
    this.onMouseLeave = () => this.setHoveredAnchor(null)
    this.element.addEventListener("mousemove", this.onMouseMove)
    this.element.addEventListener("mouseover", this.onMouseMove)
    this.element.addEventListener("mouseleave", this.onMouseLeave)
    this.scheduleNudge()
    this.openLandingAnchor()
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    this.element.removeEventListener("mousemove", this.onMouseMove)
    this.element.removeEventListener("mouseover", this.onMouseMove)
    this.element.removeEventListener("mouseleave", this.onMouseLeave)
    this.setHoveredAnchor(null)
    window.clearTimeout(this.landingFlashTimeout)
    this.closeComposer()
    this.closeThread()
    this.closeNudge()
  }

  // ---- Landing --------------------------------------------------------------

  // Put the reviewer ON the comment they were sent here for: centre its element, flash
  // the focus ring, and open its thread so the feedback itself is what they read first.
  //
  // Runs straight after decorateAnchors rather than polling for the element the way the
  // builder's `scrollToReviewAnchor` has to — this controller has just stamped every
  // `data-review-anchor-key` on the page itself, so the target either exists now or
  // never will. When it never will (the comment is on a screen this page isn't showing,
  // or its block has since been deleted) the landing quietly does nothing and the
  // reviewer simply gets the course: the Review Activity panel still lists the comment.
  //
  // The flash is deliberately independent of the "Review highlights" toggle: that
  // setting hides the standing yellow paint, and someone who turned it off still has to
  // be able to see where a deep link put them.
  openLandingAnchor() {
    const key = this.landingAnchorValue
    if (!key) return

    const anchor = this.element.querySelector(`[data-review-anchor-key="${CSS.escape(key)}"]`)
    if (!anchor) return

    // One frame out, so the scroll measures a laid-out page rather than the one mid-paint.
    requestAnimationFrame(() => {
      // The anchor may sit in content its block keeps collapsed (a timeline stage's
      // panel). The block owns that state, so it is asked to show the anchor rather
      // than having the canvas reach into its markup. Asked here, not in connect():
      // this canvas wraps the blocks, so it connects before their controllers bind
      // their actions and an event sent any earlier would reach no one.
      anchor.dispatchEvent(new CustomEvent("course-review:reveal", { bubbles: true }))
      anchor.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
      this.flashAnchor(anchor)
      // Cancel the first-run nudge WITHOUT remembering it as dismissed: its card would
      // land under the thread we are about to open, but the reviewer has not pressed
      // anything, so they are still owed the lesson on their next ordinary visit.
      // openThread's own dismissNudge then no-ops, there being no nudge or timer left.
      this.closeNudge()
      this.openThread(anchor)
    })
  }

  flashAnchor(anchor) {
    // Remove, force a reflow, re-add: that is what replays the CSS animation rather
    // than leaving a class that is already on the element doing nothing.
    window.clearTimeout(this.landingFlashTimeout)
    anchor.classList.remove("is-review-focus")
    void anchor.offsetWidth
    anchor.classList.add("is-review-focus")
    this.landingFlashTimeout = window.setTimeout(() => {
      anchor.classList.remove("is-review-focus")
    }, LANDING_FLASH_MS)
  }

  // ---- Hover ----------------------------------------------------------------

  // Which element the reviewer is pointing at is decided here rather than by CSS
  // :hover. Under :hover the innermost element always wins, so on the way to a block's
  // ⊕ the pointer crossed one of the block's parts — a matching card, a timeline panel
  // — and the block's ⊕ vanished before it could be reached. Moving out to an element
  // still switches at once; moving IN to a part of the hovered element waits until the
  // pointer rests there.
  trackHover(event) {
    const anchor = event.target.closest?.(".course-review-anchor") || null
    window.clearTimeout(this.hoverSwitchTimeout)
    if (anchor === this.hoveredAnchor) return

    if (anchor && this.hoveredAnchor?.isConnected && this.hoveredAnchor.contains(anchor)) {
      this.hoverSwitchTimeout = window.setTimeout(() => this.setHoveredAnchor(anchor), NESTED_HOVER_REST_MS)
      return
    }

    this.setHoveredAnchor(anchor)
  }

  setHoveredAnchor(anchor) {
    window.clearTimeout(this.hoverSwitchTimeout)
    this.hoveredAnchor?.classList.remove("is-review-hover")
    this.hoveredAnchor = anchor
    anchor?.classList.add("is-review-hover")
  }

  // ---- Anchor decoration ----------------------------------------------------

  // Child rows are stamped by the content-item partials with the bare child key;
  // the block id lives on the ancestor. Blocks stamp their own id. Both become a
  // `.course-review-anchor` here so one CSS contract covers them.
  decorateAnchors() {
    this.element.querySelectorAll("[data-review-anchor]").forEach((anchor) => {
      const key = this.anchorKeyFor(anchor)
      if (!key) return

      anchor.classList.add("course-review-anchor")
      anchor.dataset.reviewAnchorKey = key

      // Child rows / section fields get their ⊕ chip from here (blocks stamp theirs
      // server-side). The author has no chip anywhere, so it is skipped for them.
      if (this.canCommentValue && !this.isBlockAnchor(anchor)) this.addChipTo(anchor)
      this.paintCommentedState(anchor, key)
    })
  }

  // Where an anchor's ⊕ chip and avatar pin go. A table row cannot hold them: Chrome
  // wraps any child of a <tr> that is not a cell — positioned or not — in an anonymous
  // cell, which shoves the row's columns over. The row's last cell holds them instead,
  // which puts them at the row's right edge all the same.
  decorationHostFor(anchor) {
    if (anchor instanceof HTMLTableRowElement) return anchor.cells[anchor.cells.length - 1] || anchor

    return anchor
  }

  // An anchor's own chip or pin, wherever it currently sits — on a table row that may
  // be a cell that has since stopped being the last one.
  decorationsIn(anchor, selector) {
    const scope = anchor instanceof HTMLTableRowElement ? `:scope > :is(td, th) > ${selector}` : `:scope > ${selector}`

    return [...anchor.querySelectorAll(scope)]
  }

  // The author can add, remove or drag columns of a commented table, which changes a
  // row's last cell under its chip and pin. Each is moved to the current last cell, any
  // stray copy dropped, and a pin that left with a removed cell is painted back.
  rehostDecorations(anchor) {
    if (!(anchor instanceof HTMLTableRowElement)) return

    const host = this.decorationHostFor(anchor)
    ;[".course-review-add", ".course-review-marker"].forEach((selector) => {
      const [kept, ...strays] = this.decorationsIn(anchor, selector)
      strays.forEach((stray) => stray.remove())
      if (kept && kept.parentElement !== host) host.appendChild(kept)
    })
  }

  //
  // A row can also have changed identity: emptying a table's only row gives it a new
  // key, and a duplicated table's rows belong to a block with no id yet. Its comment
  // no longer applies, so it is cleared rather than repainted on the wrong row.
  rehostTableRowDecorations(event) {
    event.target.querySelectorAll?.("tr.course-review-anchor").forEach((row) => {
      const key = this.anchorKeyFor(row)
      if (key !== row.dataset.reviewAnchorKey) this.reanchorRow(row, key)

      this.rehostDecorations(row)
      if (row.classList.contains("is-commented")) this.paintCommentedState(row, key)
    })
  }

  reanchorRow(row, key) {
    this.clearCommentedState(row)
    if (key) {
      row.dataset.reviewAnchorKey = key
      return
    }

    delete row.dataset.reviewAnchorKey
    row.classList.remove("course-review-anchor")
  }

  isBlockAnchor(anchor) {
    return anchor.hasAttribute("data-course-builder-block-id")
  }

  // "<lesson_content_id>" for a block, "<lesson_content_id>:<child_key>" for a row,
  // and "<section>:<child_key>" for an element on a non-curriculum screen — the same
  // formats CourseReviewComment#anchor_key builds server-side.
  anchorKeyFor(anchor) {
    const raw = anchor.getAttribute("data-review-anchor")
    if (!raw) return null
    if (this.isBlockAnchor(anchor)) return raw

    // A section screen wraps its anchors in [data-review-section]. Checked before the
    // curriculum block id so an assessment question (which also sits inside a numeric
    // data-course-builder-block-id wrapper) anchors by its section, not by an id that
    // would masquerade as a lesson_content.
    const section = anchor.closest("[data-review-section]")?.getAttribute("data-review-section")
    if (section) return `${section}:${raw}`

    const blockId = anchor.closest("[data-course-builder-block-id]")?.getAttribute("data-course-builder-block-id")
    if (!blockId) return null

    return `${blockId}:${raw}`
  }

  addChipTo(anchor) {
    if (!this.hasAddButtonTemplateTarget) return
    if (this.decorationsIn(anchor, ".course-review-add").length) return

    const chip = this.addButtonTemplateTarget.content.firstElementChild.cloneNode(true)
    chip.setAttribute("aria-label", `Add a review comment on ${this.labelFor(anchor)}`)
    if (this.isInteractiveAnchor(anchor)) this.insulateChip(chip, anchor)
    this.decorationHostFor(anchor).appendChild(chip)
  }

  // An anchor the reviewer is meant to OPERATE rather than only read — a quiz option,
  // a sorting row, a flip card. Declared in the markup by `academy_review_anchor`
  // rather than sniffed from class names, so a new activity opts in explicitly.
  isInteractiveAnchor(anchor) {
    return anchor.hasAttribute("data-review-interactive")
  }

  // The chip is a child of the anchor, so the host's own handlers see everything the
  // chip does. Two of them would misread it:
  //   - a sorting row is draggable="true", so a drag begun on the chip reorders the row;
  //   - the same row listens for keydown to move itself, and keydown bubbles, so
  //     arrowing while the chip has focus silently reorders the list.
  // Neither is reachable from the chip's own markup, so it is insulated here.
  insulateChip(chip, anchor) {
    chip.setAttribute("draggable", "false")
    if (anchor.getAttribute("draggable") === "true" || anchor.hasAttribute("tabindex")) {
      chip.addEventListener("keydown", (event) => event.stopPropagation())
    }
  }

  paintCommentedState(anchor, key) {
    const entry = this.anchorsValue[key]
    // Only PENDING feedback lights an element — an anchor whose comments are all
    // resolved/dismissed is not highlighted (and, having no avatars, no marker).
    if (!entry || !entry.pending_count) return

    anchor.classList.add("is-commented")
    anchor.dataset.reviewCommentCount = entry.count
    this.markLabellingControl(anchor, true)
    this.announceCommentedState(anchor)

    if (this.decorationsIn(anchor, ".course-review-marker").length) return

    const marker = this.markerTemplateFor(key)
    if (marker) this.decorationHostFor(anchor).appendChild(marker)
  }

  markerTemplateFor(key) {
    const template = this.element.querySelector(`.course-review-marker-template[data-anchor-key="${CSS.escape(key)}"]`)

    return template ? template.content.firstElementChild.cloneNode(true) : null
  }

  // The block stamps its own human name server-side (data-review-label). Reading
  // textContent instead would scoop up every control in the subtree — the toolbar's
  // "Background color", a takeaway's placeholder — and produce a wall of noise.
  // Child rows carry no label of their own and inherit the block's.
  labelFor(anchor) {
    return anchor.closest("[data-review-label]")?.dataset.reviewLabel || "this element"
  }

  // ---- Composer -------------------------------------------------------------

  openComposer(event) {
    event.preventDefault()
    event.stopPropagation()
    // Opening a new thread is reviewer-only; the author reaches the composer only as a
    // reply hosted inside an existing thread (mountComposer with a parentId).
    if (!this.canCommentValue) return

    const trigger = event.currentTarget
    const anchor = trigger.closest("[data-review-anchor]")
    if (!anchor) return

    // Pressing a chip is the very thing the nudge teaches, so it has done its job.
    this.dismissNudge()
    this.closeThread()
    this.mountComposer({ anchor, trigger, reference: trigger })
  }

  mountComposer({ anchor, trigger, reference, parentId = "", host = null }) {
    this.closeComposer()

    const composer = this.composerTemplateTarget.content.firstElementChild.cloneNode(true)
    // A section anchor posts its section + child_key; a curriculum anchor splits its
    // "<lesson_content_id>:<child_key>" key. Exactly one of section / lessonContentId
    // is set, which is how the server routes the anchor.
    const sectionContainer = anchor.closest("[data-review-section]")
    let lessonContentId = ""
    let section = ""
    let childKey = ""
    if (sectionContainer) {
      section = sectionContainer.getAttribute("data-review-section")
      childKey = anchor.getAttribute("data-review-anchor") || ""
    } else {
      const key = anchor.dataset.reviewAnchorKey || this.anchorKeyFor(anchor)
      ;[lessonContentId, childKey = ""] = key.split(":")
    }

    Object.assign(composer.dataset, {
      courseReviewComposerCreateUrlValue: this.createUrlValue,
      courseReviewComposerAttachmentUrlValue: this.attachmentUrlValue,
      courseReviewComposerCourseIdValue: this.courseIdValue,
      courseReviewComposerLessonContentIdValue: lessonContentId,
      courseReviewComposerSectionValue: section,
      courseReviewComposerChildKeyValue: childKey,
      courseReviewComposerParentIdValue: parentId,
      courseReviewComposerPlaceholderValue: parentId ? "Reply…" : `Add a Comment on ${this.labelFor(anchor)}…`,
      courseReviewMentionsCandidatesValue: JSON.stringify(this.mentionsValue),
      courseReviewMentionsSearchUrlValue: this.mentionsSearchUrlValue
    })
    if (parentId) composer.classList.add("course-review-composer--reply")

    this.composer = composer
    this.composerAnchor = anchor
    this.composerTrigger = trigger
    anchor.classList.add("has-review-composer")
    ;(host || document.body).appendChild(composer)

    if (host) return
    this.composerCleanup = this.floatOn(reference, composer, "bottom")
  }

  closeComposer({ returnFocus = false } = {}) {
    if (!this.composer) return

    this.composerCleanup?.()
    this.composerCleanup = null
    this.composer.remove()
    this.composer = null
    this.composerAnchor?.classList.remove("has-review-composer")
    this.composerAnchor = null

    const trigger = this.composerTrigger
    this.composerTrigger = null
    if (returnFocus) trigger?.focus()
  }

  // The composer reports its own lifecycle through window events — there are no
  // Stimulus outlets in this project and the composer lives outside this subtree.
  onComposerDismissed(event) {
    this.closeComposer({ returnFocus: event.detail?.returnFocus === true })
  }

  // The yellow highlight is painted optimistically here so the element reacts the
  // instant the comment lands. The avatar cannot be: the server owns that markup,
  // and a first comment on an element has no marker template to clone yet. It
  // arrives with onCommentRefreshed.
  onCommentCreated(event) {
    const anchor = this.element.querySelector(
      `[data-review-anchor-key="${CSS.escape(event.detail.anchorKey)}"]`
    )

    this.closeComposer()
    this.closeThread()
    if (!anchor) return

    anchor.classList.add("is-commented")
    this.markLabellingControl(anchor, true)
    this.announceCommentedState(anchor)
  }

  // Fired once the review_activity stream has swapped in fresh markers. Repainting
  // every anchor rather than just the one that changed is what makes deletes and
  // resolves land too — an anchor whose last comment is gone loses its highlight
  // through the same pass.
  onCommentRefreshed() {
    // A resolve or delete from the thread's kebab lands through this same repaint;
    // the open card now shows stale data (or a comment that is gone), so close it.
    this.closeThread()
    this.reloadAnchors()
    this.syncCommentedState()
  }

  reloadAnchors() {
    const markers = this.element.querySelector("#course-review-markers")
    if (!markers?.dataset.courseReviewAnchors) return

    this.anchorsValue = JSON.parse(markers.dataset.courseReviewAnchors)
  }

  syncCommentedState() {
    this.element.querySelectorAll("[data-review-anchor-key]").forEach((anchor) => {
      const key = anchor.dataset.reviewAnchorKey
      const entry = this.anchorsValue[key]

      // Repaint when pending feedback remains; otherwise strip the highlight — this is
      // what makes a resolve/dismiss (or delete) clear the element on the next refresh.
      if (entry && entry.pending_count) {
        this.paintCommentedState(anchor, key)
      } else {
        this.clearCommentedState(anchor)
      }
    })
  }

  clearCommentedState(anchor) {
    anchor.classList.remove("is-commented")
    delete anchor.dataset.reviewCommentCount
    this.decorationsIn(anchor, ".course-review-marker").forEach((marker) => marker.remove())
    this.markLabellingControl(anchor, false)
    this.announceCommentedState(anchor)
  }

  // A block that shows its parts one at a time (a carousel, Fact or Fiction) flags the
  // way to a hidden part with pending feedback on its own navigation. The block owns
  // that markup, so it is told the state changed rather than having the canvas reach
  // in. It reads `.is-commented` itself on connect: the first paint runs before its
  // controller binds this action, the same ordering openLandingAnchor works around.
  announceCommentedState(anchor) {
    anchor.dispatchEvent(new CustomEvent("course-review:commented", { bubbles: true }))
  }

  // An anchor its block can collapse (a timeline stage's panel) hides its highlight and
  // avatar along with itself. The control that opens it — the one named by its
  // aria-labelledby — stays on screen, so that is where pending feedback is flagged.
  markLabellingControl(anchor, commented) {
    const labelId = anchor.getAttribute("aria-labelledby")
    if (!labelId) return

    document.getElementById(labelId)?.classList.toggle("has-commented-content", commented)
  }

  // ---- Thread ---------------------------------------------------------------

  handleDocumentClick(event) {
    const marker = event.target.closest?.(".course-review-marker")
    const anchor = marker?.closest("[data-review-anchor]") || this.commentedAnchorFrom(event.target)

    if (anchor) {
      event.preventDefault()
      this.openThread(anchor)
      return
    }

    // Close the floating (top-level) composer when the click lands outside it — but
    // never when it lands inside the open thread: the REPLY composer is hosted there,
    // and its own Reply button (which reveals it) would otherwise close it on the same
    // click. A click outside the thread closes both, just below.
    if (this.composer && !this.composer.contains(event.target) &&
        !event.target.closest?.(".course-review-add") &&
        !event.target.closest?.(".course-review-thread")) {
      this.closeComposer()
    }
    if (this.thread && !this.thread.contains(event.target)) this.closeThread()
  }

  commentedAnchorFrom(target) {
    // The author edits inside these elements, so a plain click must not hijack into the
    // comment thread — for them ONLY the marker (comment icon) opens it. A reviewer's
    // canvas is read-only, so click-anywhere-on-a-commented-element stays convenient.
    if (!this.canCommentValue) return null

    const anchor = target.closest?.("[data-review-anchor].is-commented")
    if (!anchor) return null
    if (target.closest?.(".course-review-add")) return null

    // …except where the reviewer is meant to OPERATE the element. Commenting on a quiz
    // option must not cost them the ability to answer it, so on answerable content the
    // marker is the only way in — the same rule the author already gets above, for the
    // same reason. Walking up from the click TARGET covers the commented anchor itself
    // (always an ancestor-or-self of the target) and, just as importantly, an answerable
    // row nested inside a commented wrapper — a commented poll question holds options.
    if (target.closest?.("[data-review-interactive]")) return null

    // A control is answerable whether or not anyone anchored it. Not every activity
    // marks its parts: a matching board anchors its cards but not the connector, and a
    // Fact or Fiction statement anchors the statement but not its answer cards — so a
    // comment would otherwise put a thread over the very cards the reviewer is trying
    // to press. Anchors say "this row is answerable"; this says "this thing is
    // a control", which is true for activities nobody has thought of yet.
    if (target.closest?.(CONTROL_SELECTOR)) return null

    return anchor
  }

  openThread(anchor) {
    this.closeThread()
    this.closeComposer()
    // Opening a thread on a block is block-level commenting in action; the nudge has
    // nothing left to teach (and its card would sit under the thread's).
    this.dismissNudge()

    const key = anchor.dataset.reviewAnchorKey
    const entry = this.anchorsValue[key]
    if (!entry) return

    const thread = this.threadTemplateTarget.content.firstElementChild.cloneNode(true)
    const list = thread.querySelector("[data-course-review-thread-list]")

    // The thread carries its own controller (it lives on <body>), so hand it the
    // comments endpoint and context the same way mountComposer hands them to the
    // composer — Resolve / Delete post through these.
    Object.assign(thread.dataset, {
      courseReviewThreadCommentsUrlValue: this.createUrlValue,
      courseReviewThreadContextValue: JSON.stringify({ course_id: this.courseIdValue })
    })

    // Every top-level comment is its own section with its own status and actions, so
    // one reviewer's feedback can be resolved while another's stays open (TMS-1616).
    entry.threads.forEach((commentThread) => list.appendChild(this.threadSectionFor(entry, commentThread)))

    this.thread = thread
    document.body.appendChild(thread)

    // The reply composer is the same composer, hosted inside the thread card with
    // parent_id set — identical mechanics, one implementation. It starts collapsed;
    // a section's Reply button moves it under that thread and points it at its comment.
    this.mountComposer({
      anchor,
      trigger: anchor,
      reference: anchor,
      parentId: String(entry.threads[0]?.id || ""),
      host: thread
    })
    this.composer?.classList.add("course-review-composer--collapsed")

    this.threadCleanup = this.centerOn(anchor, thread)
    this.revealFirstPendingThread(list)
  }

  // Sections stay in posting order so #N reads in sequence, but the list is capped in
  // height: on an element with older resolved threads the pending one — what the inbox
  // and activity links open the card for (TMS-1319, TMS-1439) — could sit below the fold.
  // Only the list scrolls; scrollIntoView would also move the page under the card.
  revealFirstPendingThread(list) {
    const section = list.querySelector("[data-thread-section][data-comment-status='pending']")
    if (!section || section === list.firstElementChild) return

    const listTop = list.getBoundingClientRect().top + parseFloat(getComputedStyle(list).paddingTop)
    list.scrollTop += section.getBoundingClientRect().top - listTop
  }

  threadSectionFor(entry, commentThread) {
    const section = this.threadSectionTemplateTarget.content.firstElementChild.cloneNode(true)
    const items = section.querySelector("[data-thread-items]")

    commentThread.comments.forEach((comment) => items.appendChild(this.threadItemFor(comment)))
    this.setUpThread(section, entry, commentThread)

    return section
  }

  threadItemFor(comment) {
    const item = this.threadItemTemplateTarget.content.firstElementChild.cloneNode(true)

    item.querySelector(".course-review-thread__avatar").textContent = comment.author_initial
    item.querySelector(".course-review-thread__author").textContent = comment.author_name
    item.querySelector(".course-review-thread__time").textContent = comment.created_label
    // body_html is built server-side by MentionParser from escaped plain text.
    item.querySelector(".course-review-thread__text").innerHTML = comment.body_html

    this.fillThreadAttachments(item, comment.files)

    return item
  }

  fillThreadAttachments(item, files) {
    if (!files || !files.length) return

    const container = item.querySelector("[data-thread-attachments]")
    files.forEach((file) => container.appendChild(this.threadAttachmentFor(file)))
    container.hidden = false
  }

  threadAttachmentFor(file) {
    const node = this.threadAttachmentTemplateTarget.content.firstElementChild.cloneNode(true)
    node.href = file.url

    if (file.image) {
      const thumb = node.querySelector(".course-review-thread__attachment-thumb")
      thumb.src = file.url
      thumb.alt = file.filename
      thumb.hidden = false
    } else {
      node.querySelector(".course-review-thread__attachment-name").textContent = file.filename
      node.querySelector(".course-review-thread__attachment-file").hidden = false
    }

    return node
  }

  // Fills a thread section's header (#N, element name, status pill) and footer
  // (Resolve/Reply/Dismiss, swapping to Reopen once closed) from its top-level comment.
  // Resolve/Dismiss are open to any reviewer; Delete — the kebab's only action — shows
  // only to the comment's own author, so the kebab is dropped when they can't delete.
  setUpThread(section, entry, commentThread) {
    section.dataset.commentId = commentThread.id
    section.dataset.commentStatus = commentThread.status

    section.querySelector("[data-thread-ordinal]").textContent = commentThread.ordinal ? `#${commentThread.ordinal}` : ""
    section.querySelector("[data-thread-label]").textContent = entry.label || ""
    this.paintThreadStatus(section, commentThread.status)
    this.toggleThreadFooter(section, commentThread.status)

    if (commentThread.deletable) {
      section.querySelector("[data-thread-toolbar]").hidden = false
    } else {
      section.querySelector("[data-thread-toolbar]")?.remove()
    }

    // A reviewer's own thread from an earlier review round is kept for reference only
    // (TMS-1730): no reply, resolve or dismiss.
    if (commentThread.read_only) section.querySelector("[data-thread-footer]")?.remove()
  }

  paintThreadStatus(section, status) {
    const pill = section.querySelector("[data-thread-status]")
    pill.textContent = { pending: "Pending", resolved: "Resolved", dismissed: "Dismissed" }[status] || "Pending"
    pill.classList.toggle("tag-pill--amber", status === "pending")
    pill.classList.toggle("course-review-thread__status--resolved", status === "resolved")
    pill.classList.toggle("course-review-thread__status--dismissed", status === "dismissed")
  }

  // Pending → Resolve + Dismiss (and its divider); once closed → Reopen only.
  toggleThreadFooter(section, status) {
    const pending = status === "pending"
    section.querySelector("[data-thread-resolve]").hidden = !pending
    section.querySelector("[data-thread-reopen]").hidden = pending
    section.querySelector("[data-thread-dismiss]").hidden = !pending
    section.querySelector("[data-thread-dismiss-divider]").hidden = !pending
  }

  closeThread() {
    if (!this.thread) return

    this.threadCleanup?.()
    this.threadCleanup = null
    this.thread.remove()
    this.thread = null
  }

  // ---- First-run nudge ------------------------------------------------------
  //
  // The ⊕ chip reveals itself only on hover, so a first-time reviewer can open a
  // course, never hover, and conclude that feedback goes in the activity panel alone
  // (TMS-1424). Until they say "Got it" — or prove they know by opening a composer or
  // a thread — the canvas picks the first commentable block, holds its hover look with
  // a nudged chip, and floats a short coach mark beside it. The dismissal is remembered
  // per reviewer in this browser, across every course: block commenting works the same
  // way everywhere, so once learned it is learned.

  scheduleNudge() {
    if (!this.canCommentValue || !this.hasNudgeTemplateTarget || this.nudgeDismissed()) return

    this.nudgeTimer = setTimeout(() => this.showNudge(), NUDGE_DELAY)
  }

  showNudge() {
    this.nudgeTimer = null
    // A composer or thread already open means the reviewer found their way in.
    if (this.nudge || this.composer || this.thread) return

    const sample = this.nudgeSampleAnchor()
    if (!sample) return

    const nudge = this.nudgeTemplateTarget.content.firstElementChild.cloneNode(true)
    // The card lives on <body>, outside this controller's subtree, so its buttons are
    // bound here rather than through data-action (the same reason the composer carries
    // its own controller).
    nudge.querySelectorAll("[data-nudge-dismiss]").forEach((button) => {
      button.addEventListener("click", () => this.dismissNudge())
    })

    sample.classList.add("is-review-nudge")
    this.nudge = nudge
    this.nudgeAnchor = sample
    document.body.appendChild(nudge)
    this.nudgeCleanup = this.floatNudgeOn(sample, nudge)
  }

  // The first commentable BLOCK that is on screen, so the coach mark lands where the
  // reviewer is already looking. A child row (a takeaway, a quiz option) makes a
  // confusing first example, so blocks win where the screen has any; a block that is
  // already commented is skipped because its yellow highlight replaces the very hover
  // outline the nudge is demonstrating.
  nudgeSampleAnchor() {
    const anchors = [...this.element.querySelectorAll(".course-review-anchor")].filter((anchor) =>
      this.decorationsIn(anchor, ".course-review-add").length && !anchor.classList.contains("is-commented")
    )
    const blocks = anchors.filter((anchor) => this.isBlockAnchor(anchor))
    const candidates = blocks.length ? blocks : anchors

    return candidates.find((anchor) => this.isOnScreen(anchor)) || candidates[0] || null
  }

  isOnScreen(element) {
    const box = element.getBoundingClientRect()

    return box.height > 0 && box.bottom > 0 && box.top < window.innerHeight
  }

  // Got it / ✕ / a chip pressed: close the card and remember it, so it never comes
  // back for this reviewer.
  dismissNudge() {
    if (!this.nudge && !this.nudgeTimer) return

    this.rememberNudgeDismissed()
    this.closeNudge()
  }

  // Tears the card down without recording anything — disconnect only. A lesson switch
  // re-renders the canvas, and a reviewer who never acknowledged the nudge should meet
  // it again on the next screen.
  closeNudge() {
    if (this.nudgeTimer) {
      clearTimeout(this.nudgeTimer)
      this.nudgeTimer = null
    }
    if (!this.nudge) return

    this.nudgeCleanup?.()
    this.nudgeCleanup = null
    this.nudge.remove()
    this.nudge = null
    this.nudgeAnchor?.classList.remove("is-review-nudge")
    this.nudgeAnchor = null
  }

  // Storage access is wrapped: private mode can reject localStorage, and the nudge
  // must still show and close normally for that visit.
  nudgeDismissed() {
    try {
      return window.localStorage.getItem(this.nudgeStorageKey) === "1"
    } catch {
      return false
    }
  }

  rememberNudgeDismissed() {
    try {
      window.localStorage.setItem(this.nudgeStorageKey, "1")
    } catch {
      // Storage unavailable — the nudge closes for this visit and may return next time.
    }
  }

  get nudgeStorageKey() {
    return `tms.courseReview.hoverNudgeDismissed.${this.viewerIdValue}`
  }

  // ---- Positioning ----------------------------------------------------------

  floatOn(reference, floating, placement) {
    const updatePosition = () => {
      computePosition(reference, floating, {
        placement,
        strategy: "fixed",
        middleware: [flip(), shift({ padding: 8 })]
      }).then(({ x, y }) => {
        Object.assign(floating.style, { left: `${x}px`, top: `${y}px` })
      })
    }

    updatePosition()

    return autoUpdate(reference, floating, updatePosition)
  }

  // The coach mark hangs just below its sample block (above it when there is no room),
  // flush with the block's RIGHT edge: it has to overlap whatever follows, and the
  // right side of left-aligned content is where a block is emptiest. Unlike the
  // composer it HIDES once the block scrolls out of the canvas — shift() alone would
  // leave the card pinned to a viewport edge with nothing to point at.
  floatNudgeOn(anchor, floating) {
    const updatePosition = () => {
      computePosition(anchor, floating, {
        placement: "bottom-end",
        strategy: "fixed",
        middleware: [offset(12), flip(), shift({ padding: 8 }), hide()]
      }).then(({ x, y, middlewareData }) => {
        Object.assign(floating.style, { left: `${x}px`, top: `${y}px` })
        floating.classList.toggle("is-offscreen", Boolean(middlewareData.hide?.referenceHidden))
      })
    }

    updatePosition()

    return autoUpdate(anchor, floating, updatePosition)
  }

  // Centre the thread card ON the element's centre point, per the design — not off to a
  // side. Placement "bottom" centres it horizontally on the anchor and drops it below;
  // the negative offset (half the anchor + half the card) pulls it back up so the two
  // centres meet. shift() then nudges it inward when the anchor sits near a viewport edge,
  // so a big block or an edge element never pushes the card off-screen. crossAxis is
  // what covers the TOP and bottom edges: for a "bottom" placement shift() otherwise only
  // moves the card sideways, and a tall card (many threads) centred on an element near
  // the top of the screen would poke above it.
  centerOn(anchor, floating) {
    const updatePosition = () => {
      computePosition(anchor, floating, {
        placement: "bottom",
        strategy: "fixed",
        middleware: [
          offset(({ rects }) => -(rects.reference.height / 2 + rects.floating.height / 2)),
          shift({ padding: 8, crossAxis: true })
        ]
      }).then(({ x, y }) => {
        Object.assign(floating.style, { left: `${x}px`, top: `${y}px` })
      })
    }

    updatePosition()

    return autoUpdate(anchor, floating, updatePosition)
  }
}
